import { isHttpError, createHttpErrorFromFetch } from '@wix/cli-http-client';
import type { HttpClient } from '@wix/cli-http-client';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  createAppDeployment,
  completeAppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/http';
import type {
  StaticFileMetadata,
  AppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/types';
import {
  createAppProject,
  getAppProject,
} from '@wix/ambassador-velo-backend-v1-app-project/http';
import { CloudProvider } from '@wix/ambassador-velo-backend-v1-app-project/types';
import {
  getAppEnvironmentByName,
  upsertAppEnvironment,
  removeAppEnvironmentVariables,
} from '@wix/ambassador-velo-backend-v1-app-environment/http';
import {
  AppEnvironmentSchema,
  AppProjectSchema,
  CompleteAppDeploymentSchema,
  CreateAppDeploymentSchema,
  CreateAppProjectSchema,
  StaticFileUploadSchema,
} from './schemas.js';

const { minRetryTimeout } = getTestOverrides();
// Velo hosting project type "Wix CLI Headless" - https://wix.atlassian.net/browse/FEDINF-13834
const APP_PROJECT_TYPE_ID_CLOUDFLARE = 'eb363dea-85a0-4159-9b05-949542be5079';
const APP_PROJECT_TYPE_ID_KUBERNETES = 'ad7b94c8-abc9-40f0-8be1-91dd7f3deb74';

export class BackendAsAServiceClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  getAppProject = async (appId: string) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getAppProject({ appProjectId: appId })),
        this.retryOptions
      );
      return AppProjectSchema.parse(data).appProject;
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 404) {
        return;
      }
      throw new CliError({
        code: CliErrorCode.FailedToGetAppProject(),
        cause: e,
      });
    }
  };

  createAppProject = async ({
    cloudProvider,
    ...appProjectOptions
  }: {
    id: string;
    displayName: string;
    slug: string;
    cloudProvider?: CloudProvider;
  }) => {
    try {
      const { data } = await this.httpClient.request(
        createAppProject({
          appProject: {
            ...appProjectOptions,
            appProjectTypeId:
              cloudProvider === CloudProvider.KUBERNETES
                ? APP_PROJECT_TYPE_ID_KUBERNETES
                : APP_PROJECT_TYPE_ID_CLOUDFLARE,
            cloudProviderOverride: cloudProvider,
          },
        })
      );

      return CreateAppProjectSchema.parse(data).appProject;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppProject(),
        cause: e,
      });
    }
  };

  createAppDeployment = async (
    appId: string,
    staticFilesMetadata: StaticFileMetadata[]
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createAppDeployment({
              appDeployment: {
                appProjectId: appId,
                staticFilesMetadata,
              },
            })
          ),
        this.retryOptions
      );

      return CreateAppDeploymentSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppDeployment(),
        cause: e,
      });
    }
  };

  completeAppDeployment = async (
    appDeployment: AppDeployment,
    staticsCompletionToken?: string
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            completeAppDeployment({
              appDeployment,
              staticsCompletionToken,
            })
          ),
        this.retryOptions
      );

      return CompleteAppDeploymentSchema.parse(data).appDeployment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedFinalizingAppDeployment(),
        cause: e,
      });
    }
  };

  postForm = async (endpointUrl: string, token: string, formData: FormData) => {
    try {
      const responseJson = await pRetry(
        async () => {
          const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            const error = await createHttpErrorFromFetch(response);
            throw error;
          }

          return response.json();
        },
        { retries: 3 }
      );

      return StaticFileUploadSchema.parse(responseJson);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUploadStaticFiles(),
        cause: e,
        info: { endpointUrl },
      });
    }
  };

  putBinaryFile = async (
    endpointUrl: string,
    fileContent: Buffer,
    contentType: string
  ) => {
    try {
      await pRetry(
        async () => {
          const response = await fetch(endpointUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
            },
            body: fileContent,
          });

          if (!response.ok) {
            const error = await createHttpErrorFromFetch(response);
            throw error;
          }
        },
        { retries: 3 }
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUploadStaticFiles(),
        cause: e,
        info: { endpointUrl },
      });
    }
  };

  // Env management

  getAppEnvironmentByName = async (appId: string, environment: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            getAppEnvironmentByName({
              appProjectId: appId,
              appEnvironmentName: environment,
            })
          ),
        this.retryOptions
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToPullEnvironment(),
        cause: e,
      });
    }
  };

  upsertAppEnvironment = async (
    appId: string,
    environment: string,
    variables: Record<string, string>
  ) => {
    try {
      const { data } = await this.httpClient.request(
        upsertAppEnvironment({
          appEnvironment: {
            appProjectId: appId,
            name: environment,
            variables,
          },
        })
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToSetEnvironmentVariable(),
        cause: e,
      });
    }
  };

  removeAppEnvironmentVariable = async (
    appId: string,
    environment: string,
    variable: string
  ) => {
    try {
      const appEnvironment = await this.getAppEnvironmentByName(
        appId,
        environment
      );

      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            removeAppEnvironmentVariables({
              appEnvironmentId: appEnvironment.id,
              appProjectId: appId,
              variables: [variable],
            })
          ),
        this.retryOptions
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToRemoveEnvironmentVariable(),
        cause: e,
      });
    }
  };
}
